import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppSelector } from "redux/hooks";
import {
  GetVideoShareListResponse,
  VideoBasePagination,
} from "api/interfaces/videoshareInterface.interface";
import { useQuery, useQueryClient } from "react-query";
import { getVideoShareAllList, getVideoShareHashTag } from "api/videoshareAPI";
import { VideoShareFilter } from "api/interfaces/videoshareInterface.interface";
import {
  CustomError,
  SORT_DIRECTION,
} from "api/interfaces/commonInterface.interface";
import { Location, useLocation } from "react-router-dom";
import VideoSharePresenter from "./VideoSharePresenter";
import { isNull } from "lodash";
import { useInfiniteQuery } from "react-query";
import { baseQueryInfo, useQueryKey } from "utils/queryUtil";
import { useMenu } from "hook/useMenu";
import useApiError from "hook/useApiError";
import useStompDebouncedRefetch, {
  StompDateSignalKey,
} from "hook/useStompDebouncedRefetch";

export default function VideoShareContainer(): JSX.Element {
  const location: Location = useLocation();

  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );

  const { notifyError, lang } = useApiError();

  const [isStatusFilter, setIsStatusFilter] = useState<VideoShareFilter>({
    Open: true,
    Ongoing: true,
    Closed: true,
  });

  const [tagFilters, setTagFilters] = useState<string[]>([]);

  const [selectedTag, setSelectedTag] = useState<string[]>([]);

  const [isSharePopup, setIsSharePopup] = useState<string>("");

  const [videoShareQueryInfo, setVideoShareQueryInfo] =
    useState<VideoBasePagination>({
      ...baseQueryInfo,
      pageLimit: 30,
      sortType: "date",
      sortDirection: SORT_DIRECTION.DESC,
      tags: null,
    });
  const queryKey = useQueryKey(videoShareQueryInfo);
  const { returnToStartPage } = useMenu({});
  useEffect(() => {
    if (!selectedAccount.userPermission.view) {
      returnToStartPage();
    }
  }, [selectedAccount.userPermission.view]);

  const joinTags = (tags: string[]) => {
    return (tags || []).join(", ");
  };

  const onChangeStatusFilter = (value: string) => {
    setIsStatusFilter((statFilter) => {
      return {
        ...statFilter,
        [value]: !statFilter[value],
      };
    });
  };

  const hashtagQuery = useQuery(
    ["videoShareHashTag", videoShareQueryInfo.keyword, selectedAccount],
    () =>
      getVideoShareHashTag({
        accountId: selectedAccount.accountId,
        search: videoShareQueryInfo.keyword as string,
      }),
    {
      retry: 0,
      refetchOnWindowFocus: false,
      onSuccess: (res: any) => {
        if (res.error === 0 && res.result !== undefined) {
          setSelectedTag([]);
          setTagFilters(
            res.result.filter(
              (r: string) => r !== "" && r !== undefined && r !== " "
            )
          );
          const changeParam = new URLSearchParams(location.search);
          if (!isNull(changeParam.get("tags"))) {
            setSelectedTag((changeParam.get("tags") as string).split(","));
          }
        }
      },
      onError: (e: any) => {
        console.log(e, "error");
      },
    }
  );

  const queryClient = useQueryClient();
  const refetch = useCallback(() => {
    queryClient.invalidateQueries(["storageStatus", selectedAccount.accountId]);
    hashtagQuery.refetch();
    refetchVideoShare();
  }, []);
  useStompDebouncedRefetch({
    stompKey: ["videoShareListUpdated"] as StompDateSignalKey[],
    refetch: refetch,
  });

  const fetchVideos = async ({ pageParam = 0 }: { pageParam?: number }) => {
    const response = await getVideoShareAllList({
      accountId: selectedAccount.accountId,
      payload: {
        ...videoShareQueryInfo,
        pageNum: pageParam,
      },
    });
    return response;
  };

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
    refetch: refetchVideoShare,
  } = useInfiniteQuery(
    ["videoShare", queryKey, selectedAccount, selectedTag],
    fetchVideos,
    {
      getNextPageParam: (lastPage: GetVideoShareListResponse) => {
        // Assuming `lastPage.page.total` gives you the total count of items
        const { total, pageNum, pageLimit } = lastPage.page;

        const nextPage = pageNum + 1;
        return (pageNum + 1) * pageLimit < total ? nextPage : undefined;
      },
      retry: 0,
      // totalSize 변환 없으면 refetch 하지말기
      refetchOnWindowFocus: false,
      enabled: true, // videoShareQueryInfo.keyword 가 빈 문자열이어도 쿼리 동작하도록
      onSuccess: (res: GetVideoShareListResponse) => {},

      onError: (e: CustomError) => {
        if (e.response?.data.error !== 5001) {
          notifyError(lang.label.videoshare.notify.search.error);
        }
      },
    }
  );

  const videoList = useMemo(() => {
    // console.log(data, " data");
    return data
      ? data.pages.flatMap((page: GetVideoShareListResponse) => page.result)
      : [];
  }, [data, videoShareQueryInfo.keyword]);
  const videoTotal = data?.pages[0]?.page?.total || 0;

  //BUG
  const onClickSearch = useCallback(
    (keyword: string) => {
      if (videoShareQueryInfo.keyword !== keyword) {
        setVideoShareQueryInfo((info: VideoBasePagination) => {
          return {
            ...info,
            keyword: keyword,
            pageNum: 0,
          };
        });
      }
    },
    [videoShareQueryInfo]
  );

  const onClickTag = useCallback(
    (item: string) => {
      if (
        item === "All" ||
        (selectedTag.includes(item) && selectedTag.length === 1)
      ) {
        setSelectedTag([]);
        return;
      }

      if (selectedTag.includes(item)) {
        setSelectedTag((orders) =>
          selectedTag.filter((order) => order !== item)
        );
        return;
      }

      if (selectedTag.includes("All")) {
        setSelectedTag((orders) =>
          selectedTag.filter((order) => order !== "All")
        );
      }

      setSelectedTag((orders) => [...orders, item]);
    },
    [selectedTag]
  );

  const onClickShare = useCallback((videoId: string, isOpen: boolean) => {
    setIsSharePopup(isOpen ? videoId : "");
  }, []);

  const handleScroll = () => {
    if (
      window.innerHeight + window.scrollY >= document.body.offsetHeight &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  };

  useEffect(() => {
    setVideoShareQueryInfo((info: VideoBasePagination) => {
      return {
        ...info,
        tags: joinTags(selectedTag),
      };
    });
  }, [selectedTag]);

  useEffect(() => {
    const container = document.getElementById("inner-container");
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [hasNextPage, isFetchingNextPage]);

  useEffect(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  return (
    <VideoSharePresenter
      videoList={videoList}
      videoTotal={videoTotal}
      isStatusFilter={isStatusFilter}
      onChangeStatusFilter={onChangeStatusFilter}
      tagFilters={tagFilters}
      selectedTag={selectedTag}
      listQueryInfo={videoShareQueryInfo}
      onClickSearch={onClickSearch}
      onClickTag={onClickTag}
      isSharePopup={isSharePopup}
      onClickShare={onClickShare}
      selectedAccount={selectedAccount}
      status={status}
    />
  );
}
